import React, { useCallback, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import trim from 'lodash/trim';
import { useSearchParams } from 'react-router-dom';
import { BusinessCollection } from '../../data-layer/business/constants';
import {
    BusinessListItem,
    BusinessListItemSkeleton,
    calculateHeight,
} from '../../ui/business/BusinessListItem';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconClose } from '../../ui/icon/IconClose';
import { IconButton } from '../../ui/input/IconButton';
import { Input } from '../../ui/input/Input';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { InfiniteList } from '../../ui/layout/InfiniteList';
import { Typography } from '../../ui/typography/Typography';
import { useMeta } from '../../utils/useMeta';
import { useBusinessCallbacks } from '../business/useBusinessCallbacks';
import { useBusinessCollection } from '../business/useBusinessCollection';

const TIMEOUT = 250;

export const Search = () => {
    const [query, setQuery] = useSearchParams();
    const fulltextQuery = query.get('query') ?? '';
    const businessCallbacks = useBusinessCallbacks();
    const collection = useBusinessCollection({
        namespace: BusinessCollection.SEARCH,
    });

    const onChangeQuery = useCallback(
        (queryText: string) => setQuery({ query: queryText }, { replace: true }),
        [setQuery],
    );

    const onSearch = useMemo(
        () =>
            debounce(
                (queryText: string = '') =>
                    collection.onLoadNext({ query: trim(queryText), limit: 20 }),
                TIMEOUT,
            ),
        [collection.onLoadNext],
    );

    useMeta({ title: 'Поиск • Яндекс Ритм' });

    useEffect(() => {
        if (collection.filters.query !== trim(fulltextQuery) && !collection.isLoading) {
            onSearch(fulltextQuery);
        }
    }, [fulltextQuery, collection.isLoading, collection.filters.query, onSearch]);

    return (
        <DashboardPage>
            <Flex
                p={2}
                width="100%"
                direction="column"
                alignItems="stretch"
                borderRadius={{ sm: 2 }}
                minHeight={{ xs: '100%', sm: '85%' }}
                background="var(--background-primary)"
            >
                <Box px={1} pb={1}>
                    <Input
                        value={query.get('query') ?? ''}
                        variant="filled"
                        placeholder="Поиск"
                        endAdornment={
                            query.get('query') ? (
                                <Box alignSelf="center">
                                    <IconButton
                                        size="xxs"
                                        variant="filled"
                                        onClick={() => onChangeQuery('')}
                                    >
                                        <IconClose />
                                    </IconButton>
                                </Box>
                            ) : undefined
                        }
                        onInput={(event) => onChangeQuery(event.currentTarget.value)}
                    />
                </Box>
                {collection.isLoading || collection.businesses.length > 0 ? (
                    <InfiniteList
                        isEndReached
                        perPage={20}
                        isLoading={collection.isLoading}
                        rowHeight={calculateHeight}
                        estimatedRowSize={calculateHeight}
                        count={collection.isLoading ? 10 : collection.businesses.length}
                        renderItem={(index) => {
                            const business = collection.businesses[index];

                            return business ? (
                                <BusinessListItem business={business} {...businessCallbacks} />
                            ) : (
                                <BusinessListItemSkeleton />
                            );
                        }}
                    />
                ) : (
                    <Flex alignItems="center" justifyContent="center" flexGrow={1}>
                        <Typography variant="body" weight="medium" color="secondary">
                            Ничего не найдено 👀
                        </Typography>
                    </Flex>
                )}
            </Flex>
        </DashboardPage>
    );
};
